import { useSelector } from 'react-redux';
import { Link, Navigate, useSearchParams } from 'react-router';

import {
  FMS_PAYMENT_REQUEST_SELF_CODES,
  FMS_VOUCHER_SELF_CODES,
} from '#/config/permissions';
import { PERSONAL_CENTER_PANELS, ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import MyClaimsPanel from '#/modules/finMgmt/claims/components/MyClaimsPanel';
import MyVouchersPanel from '#/modules/finMgmt/vouchers/components/MyVouchersPanel';
import { can } from '#/modules/infra/auth/can';
import AccountAvatar from '#/modules/infra/layout/AccountAvatar';
import PageShell from '#/modules/infra/layout/PageShell';
import { resolveAccountDisplay } from '#/modules/infra/layout/resolveAccountDisplay';

const PANEL_PROFILE = 'profile';
const PANEL_CLAIMS = 'claims';
const PANEL_VOUCHERS = 'vouchers';

const NAV_ITEMS = [
  {
    id: PANEL_PROFILE,
    title: '個人資料',
    subtitle: '帳號摘要與修改入口。',
  },
  {
    id: PANEL_CLAIMS,
    title: '我的請款',
    subtitle: '查看與送出個人請款單。',
    codes: FMS_PAYMENT_REQUEST_SELF_CODES,
  },
  {
    id: PANEL_VOUCHERS,
    title: '我的憑證',
    subtitle: '管理個人憑證與附件。',
    codes: FMS_VOUCHER_SELF_CODES,
  },
];

/** 無 `codes` 的面板（個人資料）一律可見；其餘需任一 `.self` 讀取權 */
function canSeePanel(permissions, item) {
  return !item.codes || item.codes.some((code) => can(permissions, code));
}

function panelHref(panel) {
  return ROUTES.personalCenterPanel(panel);
}

const hubCardClassName = cn(
  'flex w-full flex-col gap-1 rounded-lg border border-border bg-background p-3.5',
  'text-left transition-colors',
  'hover:border-primary-300 hover:bg-slate-50',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
);

const hubCardActiveClassName = cn(
  'border-primary-400 bg-primary-50',
  'hover:border-primary-400 hover:bg-primary-50',
);

const mobileTabClassName = cn(
  'shrink-0 rounded-md border border-transparent px-3 py-1.5',
  'text-sm font-medium text-muted-foreground whitespace-nowrap',
  'transition-colors',
  'hover:bg-slate-50 hover:text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
);

const mobileTabActiveClassName = cn(
  'border-primary-400 bg-primary-50 text-foreground',
  'hover:bg-primary-50 hover:text-foreground',
);

const profileFieldRowClassName = cn(
  'grid grid-cols-[6rem_1fr] items-baseline gap-x-4',
  'border-b border-border pb-2',
);

/**
 * 個人中心 hub：md+ 左卡／<md 頂部橫滑 tabs；右欄依 ?panel=profile|vouchers|claims。
 */
const AccountProfilePage = () => {
  const user = useSelector((state) => state.infra.user);
  const roles = useSelector((state) => state.infra.roles);
  const permissions = useSelector((state) => state.infra.permissions);
  const display = resolveAccountDisplay(user, roles);
  const [searchParams] = useSearchParams();
  const rawPanel = searchParams.get('panel');

  const navItems = NAV_ITEMS.filter((item) => canSeePanel(permissions, item));

  if (
    !PERSONAL_CENTER_PANELS.includes(rawPanel)
    || !navItems.some((item) => item.id === rawPanel)
  ) {
    return <Navigate to={ROUTES.personalCenterPanel(PANEL_PROFILE)} replace />;
  }

  const panel = rawPanel;
  const rightPaneClassName = cn(
    'mr-auto w-full',
    panel === PANEL_PROFILE && 'max-w-lg',
  );

  return (
    <PageShell variant="detail">
      <div className="flex h-full flex-col overflow-auto p-1">
        <div className="flex w-full flex-col gap-4">
          <div>
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">個人中心</span>
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              選擇功能檢視內容。
            </p>
          </div>

          {/* <md：頂部橫滑 tabs */}
          <nav
            aria-label="個人中心選單"
            className="-mx-1 overflow-x-auto md:hidden"
          >
            <div className="flex flex-nowrap gap-1.5 px-1 pb-0.5">
              {navItems.map((item) => {
                const active = panel === item.id;
                return (
                  <Link
                    key={item.id}
                    to={panelHref(item.id)}
                    replace
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      mobileTabClassName,
                      active && mobileTabActiveClassName,
                    )}
                  >
                    {item.title}
                  </Link>
                );
              })}
            </div>
          </nav>

          <div
            className={cn(
              'flex flex-col gap-4',
              'md:flex-row md:items-start',
            )}
          >
            {/* md+：左側單欄卡片 */}
            <nav
              aria-label="個人中心選單"
              className={cn(
                'hidden grid-cols-1 gap-3',
                'md:grid md:w-64 md:shrink-0 md:self-start lg:w-72',
              )}
            >
              {navItems.map((item) => {
                const active = panel === item.id;
                return (
                  <Link
                    key={item.id}
                    to={panelHref(item.id)}
                    replace
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      hubCardClassName,
                      active && hubCardActiveClassName,
                    )}
                  >
                    <p className="text-base font-semibold text-foreground">
                      {item.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.subtitle}
                    </p>
                  </Link>
                );
              })}
            </nav>

            <div className="min-w-0 flex-1 self-start">
              <div className={rightPaneClassName}>
                {panel === PANEL_CLAIMS ? (
                  <MyClaimsPanel />
                ) : panel === PANEL_VOUCHERS ? (
                  <MyVouchersPanel />
                ) : (
                  <div
                    className={cn(
                      'flex w-full flex-col items-stretch gap-4',
                      'rounded-lg border border-border bg-background p-6',
                    )}
                  >
                    <div className="flex w-full justify-end">
                      <Button asChild>
                        <Link to={ROUTES.personalCenterEdit}>修改資料</Link>
                      </Button>
                    </div>
                    <div className="flex justify-center">
                      <AccountAvatar display={display} size="lg" />
                    </div>
                    <dl className="w-full space-y-3 text-sm">
                      <div className={profileFieldRowClassName}>
                        <dt className="text-muted-foreground">姓名</dt>
                        <dd className="truncate text-right font-medium text-foreground">
                          {display.name}
                        </dd>
                      </div>
                      <div className={profileFieldRowClassName}>
                        <dt className="text-muted-foreground">帳號</dt>
                        <dd className="truncate text-right font-medium text-foreground">
                          {display.account || '—'}
                        </dd>
                      </div>
                      <div className={profileFieldRowClassName}>
                        <dt className="text-muted-foreground">暱稱</dt>
                        <dd className="truncate text-right font-medium text-foreground">
                          {display.nickname}
                        </dd>
                      </div>
                      <div className={profileFieldRowClassName}>
                        <dt className="text-muted-foreground">工作信箱</dt>
                        <dd className="truncate text-right font-medium text-foreground">
                          {display.workEmail}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[6rem_1fr] items-baseline gap-x-4">
                        <dt className="text-muted-foreground">職稱</dt>
                        <dd className="truncate text-right font-medium text-foreground">
                          {display.title}
                        </dd>
                      </div>
                    </dl>
                    {display.isMock ? (
                      <p className="text-center text-xs text-muted-foreground">
                        （預覽資料：尚未登入或開發模式）
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AccountProfilePage;
